import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building,
  Clock,
  FolderKanban,
  Handshake,
  Lock,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Target,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import {
  useDeleteMeeting,
  useGrantMeetingAccess,
  useMeeting,
  useRequestMeetingAccess,
  useUpdateMeeting,
} from '../hooks/useMeetingLog';
import { useUsers } from '../hooks/useUsers';
import { ApiError } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import {
  meetingTypeLabel,
  type Meeting,
  type MeetingAccessLevel,
  type MeetingInput,
} from '../lib/meetingLogApi';
import { MeetingAccessCard } from '../components/meetings/MeetingAccessCard';
import { MeetingFormModal } from '../components/meetings/MeetingFormModal';
import { MeetingNotesTab } from '../components/meetingNotes/MeetingNotesTab';
import { MeetingTaskSuggestions } from '../components/meetings/MeetingTaskSuggestions';
import {
  GenerateMinutesButton,
  MeetingMinutesCard,
} from '../components/meetings/MeetingMinutesCard';
import { Markdown } from '../components/chat/Markdown';
import { SkeletonCard } from '../components/ui/Skeleton';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { Select } from '../components/ui/Select';
import { useProjects } from '../hooks/useProjects';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ro-RO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
}

/** The meeting's project, re-pickable in place (no need to open the full edit form). */
function ProjectPicker({
  value,
  saving,
  onChange,
}: {
  value: string;
  saving: boolean;
  onChange: (projectId: string | null) => void;
}) {
  const { t } = useI18n();
  const { data } = useProjects({ sort: 'name', limit: 100 });
  const options = (data?.projects ?? []).map((p) => ({
    value: p.id,
    label: p.code ? `${p.code} — ${p.name}` : p.name,
  }));
  return (
    <Select
      label={t('meetings.project')}
      hideLabel
      size="sm"
      searchable
      clearable
      disabled={saving}
      value={value}
      onChange={(v) => onChange(v || null)}
      placeholder={t('meetings.form.noProject')}
      options={options}
    />
  );
}

function Card({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <h2 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {icon}
        {title}
      </h2>
      {children}
    </section>
  );
}

/**
 * The meeting exists but the caller is not on it (their role sees only the
 * meetings they are part of) — usually because someone pasted its link in a
 * comment (BUG-15). Say so, and let them ask the person responsible.
 */
function AccessRequired({ meetingId }: { meetingId: string }) {
  const { t } = useI18n();
  const request = useRequestMeetingAccess(meetingId);
  const sent = request.data && !request.data.alreadyHasAccess;
  return (
    <div className="mt-6 max-w-lg rounded-lg border border-gray-200 bg-white p-6">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
          <Lock size={20} />
        </div>
        <div>
          <h1 className="text-base font-semibold text-gray-900">{t('meetings.access.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">{t('meetings.access.body')}</p>
          {sent ? (
            <p className="mt-4 text-sm font-medium text-green-700">
              {t('meetings.access.sent', { owner: request.data.ownerName })}
            </p>
          ) : (
            <button
              type="button"
              onClick={() =>
                request.mutate(undefined, {
                  // Added in the meantime: just load the meeting.
                  onSuccess: (r) => r.alreadyHasAccess && window.location.reload(),
                })
              }
              disabled={request.isPending}
              className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-fixed-white hover:bg-brand-700 disabled:opacity-50"
            >
              {request.isPending ? t('meetings.access.sending') : t('meetings.access.request')}
            </button>
          )}
          {request.isError && (
            <p className="mt-2 text-sm text-red-600">{t('meetings.access.failed')}</p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The owner opened the link from an access request (`?acces=<userId>`): one
 * click puts that colleague on the meeting. Hidden once they are on it.
 */
function AccessRequestBanner({ meeting, canEdit }: { meeting: Meeting; canEdit: boolean }) {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const requesterId = params.get('acces');
  const { data: users } = useUsers();
  const grant = useGrantMeetingAccess(meeting.id);
  const [added, setAdded] = useState<string | null>(null);

  const dismiss = () => {
    const next = new URLSearchParams(params);
    next.delete('acces');
    setParams(next, { replace: true });
  };

  if (added) {
    return (
      <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
        <span>{t('meetings.access.added', { name: added })}</span>
        <button type="button" onClick={() => setAdded(null)} aria-label={t('common.close')}>
          <X size={16} />
        </button>
      </div>
    );
  }
  if (!requesterId || !canEdit) return null;
  if (meeting.teamMembers.some((m) => m.userId === requesterId)) return null;
  if (meeting.owner.id === requesterId) return null;
  if (meeting.access.some((a) => a.userId === requesterId)) return null;
  const name = users?.find((u) => u.id === requesterId)?.name;
  if (!name) return null;

  const answer = (level: MeetingAccessLevel) =>
    grant.mutate(
      { userId: requesterId, level },
      {
        onSuccess: () => {
          dismiss();
          setAdded(name);
        },
      },
    );

  return (
    <div className="mt-4 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-gray-800">{t('meetings.access.banner', { name })}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={grant.isPending}
            onClick={() => answer('VIEW')}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-fixed-white hover:bg-brand-700 disabled:opacity-50"
          >
            <UserPlus size={15} /> {t('meetings.share.view')}
          </button>
          <button
            type="button"
            disabled={grant.isPending}
            onClick={() => answer('EDIT')}
            className="inline-flex items-center gap-1.5 rounded-md border border-brand-600 px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-white disabled:opacity-50"
          >
            {t('meetings.share.edit')}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="rounded-md px-2 py-1.5 text-gray-500 hover:bg-white hover:text-gray-700"
            aria-label={t('common.close')}
          >
            <X size={16} />
          </button>
        </div>
      </div>
      {grant.isError && <p className="mt-2 text-red-600">{t('meetings.access.failed')}</p>}
    </div>
  );
}

export function MeetingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { data: meeting, isLoading, isError, error } = useMeeting(id);
  // View-only access to this meeting narrows what the role allows in general.
  const readOnly = meeting?.viewerCanEdit === false;
  const canEdit = can('meetings', 'canEdit') && !readOnly;
  const canDelete = can('meetings', 'canDelete') && !readOnly;
  const canViewNotes = can('meetings', 'canViewNotes');
  const canEditNotes = can('meetings', 'canEditNotes') && !readOnly;
  const confirm = useConfirm();

  const updateMutation = useUpdateMeeting(id ?? '');
  const deleteMutation = useDeleteMeeting();
  const [showEdit, setShowEdit] = useState(false);

  async function handleEdit(input: MeetingInput) {
    await updateMutation.mutateAsync(input);
    setShowEdit(false);
  }

  async function handleDelete() {
    if (!meeting) return;
    if (!(await confirm({ message: t('meetings.confirmDelete'), danger: true }))) return;
    await deleteMutation.mutateAsync(meeting.id);
    navigate('/meetings');
  }

  if (isLoading) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <SkeletonCard lines={4} />
      </div>
    );
  }

  if (isError || !meeting) {
    const accessRequired =
      error instanceof ApiError && error.code === 'MEETING_ACCESS_REQUIRED' && id;
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <Link to="/meetings" className="text-sm text-brand-700 hover:underline">
          ← {t('meetings.back')}
        </Link>
        {accessRequired ? (
          <AccessRequired meetingId={id} />
        ) : (
          <p className="mt-4 text-red-600">{t('meetings.notFound')}</p>
        )}
      </div>
    );
  }

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <Link
        to="/meetings"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft size={14} /> {t('meetings.back')}
      </Link>

      {/* Header */}
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <Handshake size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{meeting.title}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
              <span className="inline-flex items-center gap-1">
                <Clock size={14} className="text-gray-400" />
                {formatDate(meeting.startAt)}, {formatTime(meeting.startAt)}
                {meeting.endAt && `–${formatTime(meeting.endAt)}`}
              </span>
              {meeting.location && (
                <span className="inline-flex items-center gap-1">
                  <MapPin size={14} className="text-gray-400" /> {meeting.location}
                </span>
              )}
              <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                {meetingTypeLabel(meeting.meetingType)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              type="button"
              onClick={() => setShowEdit(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Pencil size={15} /> {t('common.edit')}
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={deleteMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 size={15} /> {t('common.delete')}
            </button>
          )}
        </div>
      </div>

      <AccessRequestBanner meeting={meeting} canEdit={canEdit} />

      {/* Cards */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title={t('meetings.details')} icon={<Users size={14} />}>
          <dl className="space-y-2 text-sm">
            <div>
              <dt className="text-xs text-gray-500">{t('meetings.responsible')}</dt>
              <dd className="font-medium text-gray-900">{meeting.owner.name}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">{t('meetings.team')}</dt>
              <dd className="text-gray-800">
                {meeting.teamMembers.length === 0 ? (
                  <span className="text-gray-400">—</span>
                ) : (
                  <ul className="space-y-0.5">
                    {meeting.teamMembers.map((m) => (
                      <li key={m.id}>
                        <Link
                          to={`/people/${m.id}`}
                          className="hover:text-brand-700 hover:underline"
                        >
                          {m.fullName}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">{t('meetings.client')}</dt>
              <dd>
                {meeting.client ? (
                  <Link
                    to={`/clients/${meeting.client.id}`}
                    className="inline-flex items-center gap-1 text-brand-700 hover:underline"
                  >
                    <Building size={13} /> {meeting.client.name}
                  </Link>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">{t('meetings.project')}</dt>
              <dd className="space-y-1">
                {canEdit && (
                  <ProjectPicker
                    value={meeting.project?.id ?? ''}
                    saving={updateMutation.isPending}
                    onChange={(projectId) => void updateMutation.mutateAsync({ projectId })}
                  />
                )}
                {meeting.project ? (
                  <Link
                    to={`/projects/${meeting.project.id}`}
                    className="inline-flex items-center gap-1 text-brand-700 hover:underline"
                  >
                    <FolderKanban size={13} />
                    {meeting.project.code ? `${meeting.project.code} — ` : ''}
                    {meeting.project.name}
                  </Link>
                ) : (
                  !canEdit && <span className="text-gray-400">—</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">{t('meetings.lead')}</dt>
              <dd>
                {meeting.lead ? (
                  <Link
                    to={`/leads/${meeting.lead.id}`}
                    className="inline-flex items-center gap-1 text-brand-700 hover:underline"
                  >
                    <Target size={13} />
                    <span className="font-mono text-xs">{meeting.lead.identifier}</span>
                    {meeting.lead.title}
                  </Link>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </dd>
            </div>
            {meeting.createdBy && (
              <div>
                <dt className="text-xs text-gray-500">{t('meetings.createdBy')}</dt>
                <dd className="text-gray-800">{meeting.createdBy.name}</dd>
              </div>
            )}
          </dl>
        </Card>

        <Card title={t('meetings.externalAttendees')} icon={<Handshake size={14} />}>
          {meeting.attendees.length === 0 ? (
            <p className="text-sm text-gray-400">{t('meetings.noAttendees')}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {meeting.attendees.map((a) => (
                <li key={a.id} className="py-2 first:pt-0 last:pb-0">
                  <div className="text-sm font-medium text-gray-900">{a.fullName}</div>
                  <div className="text-xs text-gray-500">
                    {[a.role, a.companyName].filter(Boolean).join(' · ')}
                    {a.client && (
                      <>
                        {a.role || a.companyName ? ' · ' : ''}
                        <Link
                          to={`/clients/${a.client.id}`}
                          className="text-brand-700 hover:underline"
                        >
                          {a.client.name}
                        </Link>
                      </>
                    )}
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-gray-600">
                    {a.email && (
                      <a
                        href={`mailto:${a.email}`}
                        className="inline-flex items-center gap-1 hover:underline"
                      >
                        <Mail size={11} className="text-gray-400" /> {a.email}
                      </a>
                    )}
                    {a.phone && (
                      <a
                        href={`tel:${a.phone}`}
                        className="inline-flex items-center gap-1 hover:underline"
                      >
                        <Phone size={11} className="text-gray-400" /> {a.phone}
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={t('meetings.manualNotes')}>
          {meeting.notes ? (
            <div className="text-sm text-gray-800">
              <Markdown content={meeting.notes} />
            </div>
          ) : (
            <p className="text-sm text-gray-400">—</p>
          )}
        </Card>
      </div>

      <div className="mt-4">
        <MeetingAccessCard meeting={meeting} canManage={canEdit} />
      </div>

      {/* Transcript + AI notes (shared meeting-note pipeline, meeting-owned) */}
      <section className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="text-base font-semibold text-gray-900">{t('meetings.transcripts')}</h2>
        <p className="mt-1 text-sm text-gray-500">{t('meetings.transcriptsHint')}</p>
        <div className="mt-4">
          {canViewNotes ? (
            <MeetingNotesTab
              owner={{ meetingId: meeting.id }}
              projectId={meeting.project?.id}
              readOnly={!canEditNotes}
              extraActions={
                canEditNotes ? <GenerateMinutesButton meeting={meeting} variant="outline" /> : null
              }
            />
          ) : (
            <p className="text-sm text-gray-500">{t('meetings.notesForbidden')}</p>
          )}
        </div>
      </section>

      {/* The minutes document, written by the AI on request and downloaded as Word */}
      {canViewNotes && <MeetingMinutesCard meeting={meeting} readOnly={!canEditNotes} />}

      {/* AI task suggestions from the transcript — reviewed by hand, never auto-created */}
      {canViewNotes && <MeetingTaskSuggestions meeting={meeting} readOnly={!canEditNotes} />}

      {showEdit && (
        <MeetingFormModal
          initial={meeting}
          submitting={updateMutation.isPending}
          onClose={() => setShowEdit(false)}
          onSubmit={handleEdit}
        />
      )}
    </div>
  );
}
